import { ContractState, ContractVersionViewModel, ContractViewModel } from '@ail/contracts-api'
import { UnitAccountIndexViewModel } from '@ail/customers-api'
import { useTranslation } from 'react-i18next'
import { isDateValid, printDate } from '../../../../helpers/Utils'

interface ContractCostCenterCellProps {
  contract: ContractViewModel
  units: Record<string, UnitAccountIndexViewModel | undefined>
  onHistoryToggle: (contractId?: string) => void
  showHistory?: boolean
}

export function ContractCostCenterCell(props: ContractCostCenterCellProps) {
  const { contract, units } = props
  const { t } = useTranslation('common')

  const filteredUnits: UnitAccountIndexViewModel[] = []
  const filteredVersions: ContractVersionViewModel[] = []
  const allowedStates = [ContractState.Running, ContractState.Extended, ContractState.Initialized]

  const hasVersions = contract.versions && contract.versions?.length > 1
  const hasDifferentOwner = contract.versions?.some((q) => q.owner !== contract.owner)
  const isAllowedState = allowedStates.includes(contract.state)
  const hasFutureVersions = contract.versions?.some((q) => new Date(q.validFrom).getTime() > new Date().getTime())

  if (hasVersions && hasDifferentOwner && isAllowedState && hasFutureVersions) {
    const source = [...contract.versions!]
    const sourceOrdered = source.sort((a, b) => {
      const aValidFrom = new Date(a.validFrom).getTime()
      const bValidFrom = new Date(b.validFrom).getTime()
      const aValidTo = a.validTo ? new Date(a.validTo).getTime() : 0
      const bValidTo = b.validTo ? new Date(b.validTo).getTime() : 0

      return bValidFrom - aValidFrom || bValidTo - aValidTo
    })
    sourceOrdered.forEach((q) => {
      const version = filteredVersions[filteredVersions.length - 1]

      if (!filteredVersions.length || version.owner !== q.owner) {
        filteredVersions.push(q)
      } else {
        const newVersion = { ...version }
        if (filteredVersions.filter((el) => el.owner === q.owner).length > 1) {
          newVersion.validFrom =
            new Date(q.validFrom).getTime() < new Date(version?.validFrom).getTime() ? version.validFrom : q.validFrom
        }
        if (new Date(q.validFrom).getTime() < new Date(version?.validFrom).getTime()) {
          newVersion.validFrom = q.validFrom
        }
        if (q.validTo && version.validTo && new Date(q.validTo).getTime() > new Date(version?.validTo).getTime()) {
          newVersion.validTo = q.validTo
        }
        filteredVersions[filteredVersions.length - 1] = newVersion
      }
      const unit = q.owner ? units[q.owner] : undefined
      if (unit) {
        filteredUnits.push(unit)
      }
    })
  }
  const costCenter = contract.owner ? units[contract.owner]?.unit?.name : t('NO_COST_CENTER')
  return (
    <span className="costCenter" title={costCenter}>
      <span
        className={`ellipsis ${
          filteredVersions.some((q) => new Date(q.validFrom).getTime() > new Date().getTime()) ? 'toBeChanged' : ''
        }`}
      >
        {costCenter}
      </span>
      {filteredVersions?.length > 1 && (
        <i
          style={{
            color: `${
              filteredVersions.filter((q) => new Date(q.validFrom).getTime() > new Date().getTime()).length > 0
                ? ''
                : 'black'
            }`,
          }}
          className="icon-change-generic"
          onClick={() => props.onHistoryToggle(props.showHistory ? undefined : contract.id)}
        >
          {!!props.showHistory && (
            <div className="tooltip">
              <ul className="lastMile">
                {filteredVersions?.map((version) => (
                  <li>
                    <div className="flex">
                      <div className="dataMile">
                        {filteredUnits.find((unit) => unit?.value === version?.owner)?.unit?.name ||
                          t('NO_COST_CENTER')}
                      </div>
                      <div className="dataMile">
                        {version?.validTo &&
                          version?.validFrom &&
                          isDateValid(version.validTo) &&
                          isDateValid(version.validFrom) && (
                            <>
                              {t('TO')} {printDate(new Date(version.validTo))}
                              <br />
                            </>
                          )}
                        {!isDateValid(version.validFrom) && version?.owner
                          ? t('FIRST_VERSION')
                          : `${t('FROM')} ${printDate(new Date(version.validFrom))}`}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </i>
      )}
    </span>
  )
}
